// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
import { format } from "date-fns";
import { Activity, Search, X } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { DonutProgress } from "@/components/ui/donut-progress";
import { Input } from "@/components/ui/input";
import { useSidebar } from "@/components/ui/sidebar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useDeepResearchReports } from "@/hooks/api/use-deep-research";

function getStatusBadge(status: string) {
  const statusConfig = {
    draft: { label: "Draft", variant: "secondary" as const },
    pending_review: {
      label: "Pending",
      variant: "opportunity-active" as const,
    },
    queued: { label: "Queued", variant: "opportunity-active" as const },
    active: { label: "Active", variant: "opportunity-won" as const },
    complete: { label: "Complete", variant: "opportunity-won" as const },
    paused: { label: "Paused", variant: "secondary" as const },
  };

  const config = statusConfig[status as keyof typeof statusConfig];
  if (!config) {
    // Format unknown statuses by replacing underscores and capitalizing
    const formattedStatus = status
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
    return <Badge variant="default">{formattedStatus}</Badge>;
  }

  return <Badge variant={config.variant}>{config.label}</Badge>;
}

export function DeepResearchDataTable() {
  const navigate = useNavigate();
  const { state, isMobile } = useSidebar();
  const [searchTerm, setSearchTerm] = useState("");
  const { data: datasets = [], isLoading, error } = useDeepResearchReports();

  const filteredDatasets = datasets.filter(
    (dataset) =>
      dataset.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      dataset.created_by.email
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (dataset.created_by.name || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()),
  );

  const handleRowClick = (event: React.MouseEvent, datasetId: string) => {
    const url = `/deep-research/${datasetId}`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };

  return (
    <Card className="h-full relative">
      <div className="p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Filter by dataset name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-[250px] border-0 border-b border-border shadow-none rounded-none pl-7 pr-8 h-10 placeholder:text-muted-foreground focus:ring-0 focus-visible:ring-0 focus:border-muted-foreground transition-colors bg-transparent hover:border-muted-foreground"
            />
            {searchTerm && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-0 top-1/2 -translate-y-1/2 h-6 w-6 p-0 hover:bg-transparent"
                onClick={() => setSearchTerm("")}
              >
                <X className="h-4 w-4 text-muted-foreground hover:text-foreground/80" />
              </Button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-sm text-muted-foreground text-right">
            {isLoading
              ? "Loading..."
              : filteredDatasets.length > 0
                ? `${filteredDatasets.length.toLocaleString()} ${
                    filteredDatasets.length === 1 ? "dataset" : "datasets"
                  } found`
                : "No datasets found"}
          </div>
        </div>
      </div>

      <div
        className="rounded-md border max-h-[calc(100vh-11rem)] overflow-auto"
        style={{
          maxWidth: isMobile
            ? "calc(100vw - 2rem)"
            : state === "collapsed"
              ? "calc(100vw - var(--sidebar-width-icon) - 2rem)"
              : "calc(100vw - var(--sidebar-width) - 2rem)",
          minWidth: "640px",
        }}
      >
        <Table className="w-full table-fixed">
          {isLoading ? (
            <>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead style={{ width: 300 }}>
                    <div className="h-4 w-full bg-muted rounded animate-pulse" />
                  </TableHead>
                  <TableHead style={{ width: 100 }}>
                    <div className="h-4 w-full bg-muted rounded animate-pulse" />
                  </TableHead>
                  <TableHead style={{ width: 120 }}>
                    <div className="h-4 w-full bg-muted rounded animate-pulse" />
                  </TableHead>
                  <TableHead style={{ width: 150 }}>
                    <div className="h-4 w-full bg-muted rounded animate-pulse" />
                  </TableHead>
                  <TableHead style={{ width: 100 }}>
                    <div className="h-4 w-full bg-muted rounded animate-pulse" />
                  </TableHead>
                  <TableHead style={{ width: 120 }}>
                    <div className="h-4 w-full bg-muted rounded animate-pulse" />
                  </TableHead>
                  <TableHead style={{ width: 80 }}>
                    <div className="h-4 w-full bg-muted rounded animate-pulse" />
                  </TableHead>
                  <TableHead style={{ width: 150 }}>
                    <div className="h-4 w-full bg-muted rounded animate-pulse" />
                  </TableHead>
                  <TableHead style={{ width: 120 }}>
                    <div className="h-4 w-full bg-muted rounded animate-pulse" />
                  </TableHead>
                  <TableHead style={{ width: 140 }}>
                    <div className="h-4 w-full bg-muted rounded animate-pulse" />
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="bg-card">
                {Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <div className="h-4 w-full bg-muted rounded animate-pulse" />
                    </TableCell>
                    <TableCell>
                      <div className="h-4 w-full bg-muted rounded animate-pulse" />
                    </TableCell>
                    <TableCell>
                      <div className="h-4 w-full bg-muted rounded animate-pulse" />
                    </TableCell>
                    <TableCell>
                      <div className="h-4 w-full bg-muted rounded animate-pulse" />
                    </TableCell>
                    <TableCell>
                      <div className="h-4 w-full bg-muted rounded animate-pulse" />
                    </TableCell>
                    <TableCell>
                      <div className="h-4 w-full bg-muted rounded animate-pulse" />
                    </TableCell>
                    <TableCell>
                      <div className="h-4 w-full bg-muted rounded animate-pulse" />
                    </TableCell>
                    <TableCell>
                      <div className="h-4 w-full bg-muted rounded animate-pulse" />
                    </TableCell>
                    <TableCell>
                      <div className="h-4 w-full bg-muted rounded animate-pulse" />
                    </TableCell>
                    <TableCell>
                      <div className="h-4 w-full bg-muted rounded animate-pulse" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </>
          ) : (
            <>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead
                    className="text-xs font-medium"
                    style={{ width: 300 }}
                  >
                    Name
                  </TableHead>
                  <TableHead
                    className="text-xs font-medium"
                    style={{ width: 100 }}
                  >
                    Type
                  </TableHead>
                  <TableHead
                    className="text-xs font-medium"
                    style={{ width: 120 }}
                  >
                    Status
                  </TableHead>
                  <TableHead
                    className="text-xs font-medium"
                    style={{ width: 150 }}
                  >
                    Entity Type
                  </TableHead>
                  <TableHead
                    className="text-xs font-medium text-right"
                    style={{ width: 100 }}
                  >
                    Records
                  </TableHead>
                  <TableHead
                    className="text-xs font-medium text-right"
                    style={{ width: 120 }}
                  >
                    AI Columns
                  </TableHead>
                  <TableHead
                    className="text-xs font-medium text-center"
                    style={{ width: 80 }}
                  >
                    Completion
                  </TableHead>
                  <TableHead
                    className="text-xs font-medium"
                    style={{ width: 150 }}
                  >
                    Created By
                  </TableHead>
                  <TableHead
                    className="text-xs font-medium"
                    style={{ width: 120 }}
                  >
                    Created
                  </TableHead>
                  <TableHead
                    className="text-xs font-medium"
                    style={{ width: 140 }}
                  >
                    Last Refreshed
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="bg-card">
                {error ? (
                  <TableRow>
                    <TableCell
                      colSpan={10}
                      className="h-24 text-center text-muted-foreground"
                    >
                      Error loading datasets: {error.message || "Unknown error"}
                    </TableCell>
                  </TableRow>
                ) : filteredDatasets.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={10}
                      className="h-24 text-center text-muted-foreground"
                    >
                      No datasets found.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredDatasets.map((dataset) => (
                    <TableRow
                      key={dataset.id}
                      className="hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={(e) => handleRowClick(e, dataset.id)}
                    >
                      <TableCell className="text-xs">
                        <span className="font-medium">{dataset.name}</span>
                      </TableCell>
                      <TableCell className="text-xs">
                        <Badge
                          variant="secondary"
                          className="flex items-center gap-1 w-fit"
                        >
                          {dataset.dataset_type === "dynamic" && (
                            <Activity className="h-3 w-3" />
                          )}
                          {dataset.dataset_type === "static"
                            ? "Static"
                            : "Dynamic"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">
                        {getStatusBadge(dataset.status)}
                      </TableCell>
                      <TableCell className="text-xs capitalize text-muted-foreground">
                        {dataset.entity_type}
                      </TableCell>
                      <TableCell className="text-xs text-right tabular-nums">
                        {dataset.record_count !== undefined &&
                        dataset.record_count !== null
                          ? dataset.record_count.toLocaleString()
                          : dataset.status === "complete" ||
                              dataset.status === "active" ||
                              dataset.status === "paused"
                            ? "Loading..."
                            : "-"}
                      </TableCell>
                      <TableCell className="text-xs text-right tabular-nums">
                        {dataset.ai_field_count !== undefined &&
                        dataset.ai_field_count !== null
                          ? dataset.ai_field_count.toLocaleString()
                          : "-"}
                      </TableCell>
                      <TableCell className="text-xs">
                        <div className="flex justify-center">
                          {(dataset.status === "pending_review" ||
                            dataset.status === "queued") &&
                          dataset.completion !== undefined &&
                          dataset.completion !== null ? (
                            <DonutProgress
                              value={dataset.completion}
                              size={32}
                              strokeWidth={3}
                              showLabel={true}
                            />
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {dataset.created_by.name || dataset.created_by.email}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {format(new Date(dataset.created_at), "MMM d, yyyy")}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {dataset.last_refreshed_at
                          ? format(
                              new Date(dataset.last_refreshed_at),
                              "MMM d, h:mm a",
                            )
                          : "-"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </>
          )}
        </Table>
      </div>
    </Card>
  );
}
